// Import from the sibling leaf module (not the barrel "@repo/locale") to avoid a
// circular dependency (index → autodetectLocale → index) that would leave consts
// like NON_LOCALIZED_PREFIXES uninitialized during eager module evaluation.
import type { LanguageCode } from "./locales";
import {
  COUNTRIES,
  DEFAULT_LANGUAGE_CODE,
  getDefaultLocaleForCountry,
  LOCALE_COOKIE as LOCALE_COOKIE_NAME,
  normalizeCode,
} from "./locales";

const LOCALE_COOKIE = "default-locale";

// function getCountryFromHeaders(request: RequestLike): string | undefined {
//   return (
//     request.headers.get("x-vercel-ip-country") ||
//     request.headers.get("cf-ipcountry") ||
//     request.headers.get("cloudfront-viewer-country") ||
//     undefined
//   );
// }
function getCountryFromHeaders(request: RequestLike): string | undefined {
  const fastlyCountry = request.headers.get("country");

  const xCountryCode = request.headers.get("x-country-code");

  return fastlyCountry || xCountryCode || undefined;
}
type RequestLike = {
  cookies: {
    get: (name: string) => { value: string } | undefined;
  };
  headers: {
    get: (name: string) => string | null;
  };
};

export function resolveCulture(request: RequestLike): LanguageCode {
  let resolvedCode: LanguageCode | null = null;

  // 1️⃣ Cookie (highest priority)
  const cookieLocale = request.cookies.get(LOCALE_COOKIE)?.value;
  resolvedCode = normalizeCode(cookieLocale);

  // 2️⃣ IP-based detection
  if (!resolvedCode) {
    const country = getCountryFromHeaders(request);

    const match = COUNTRIES.find((c) => c.countryCode === country);

    if (match) {
      resolvedCode = match.localeCode as LanguageCode;
    } else {
      const clFallbackCountries = [
        "AR",
        "BO",
        "BR",
        "CO",
        "CR",
        "DO",
        "EC",
        "GT",
        "HN",
        "MX",
        "NI",
        "PA",
        "PY",
        "PE",
        "SV",
        "UY",
        "VE",
        "ES",
      ];

      if (country && clFallbackCountries.includes(country)) {
        resolvedCode = "es_cl" as LanguageCode;
      } else {
        // All other countries / failed detection → English
        resolvedCode = "en_us" as LanguageCode;
      }
    }
  }

  // 3️⃣ Browser locale
  if (!resolvedCode) {
    const acceptLang = request.headers.get("accept-language");

    if (acceptLang) {
      const browserLocale = acceptLang.split(",")[0];
      const country = normalizeCode(browserLocale);
      const match = COUNTRIES.find((c) => c.countryCode === country);

      if (match) {
        resolvedCode = match.localeCode as LanguageCode;
      }
    }
  }

  // 4️⃣ Final fallback
  if (!resolvedCode) {
    resolvedCode = "es_cl" as LanguageCode;
  }

  return resolvedCode;
}

/**
 * Resolve the DEFAULT locale used only to pick a redirect target for the
 * base-path / invalid-slug cases (see localeMiddleware). Order is intentionally:
 *   1) locale cookie (returning users)
 *   2) Fastly "country" header → country→lang mapping
 *   3) defined defaults
 * `accept-language` is intentionally NOT used here, per the agreed rule.
 */
export function resolveDefaultLocale(request: RequestLike): LanguageCode {
  // 1) locale cookie (should already exist for returning users)
  const cookie =
    normalizeCode(request.cookies.get(LOCALE_COOKIE_NAME)?.value) ??
    normalizeCode(request.cookies.get(LOCALE_COOKIE)?.value);
  if (cookie) return cookie;

  // 2) Fastly "country" header (already consumed today)
  const country = getCountryFromHeaders(request); // reads "country" / "x-country-code"
  const byCountry = country ? getDefaultLocaleForCountry(country) : null;
  if (byCountry) return byCountry;

  // 3) defined defaults
  return DEFAULT_LANGUAGE_CODE; // "es_cl"
}
